import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faPlus, faXmark, faCheck, faCircleCheck, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import {
  fetchTrialSets,
  fetchBlindBatch,
  saveBlindLabels,
  constructSet,
  FACETS,
  type TrialSetSummary,
  type BlindBatch,
  type SkillFacet,
} from '../api/skillTrials';

type Toast = { kind: 'info' | 'error'; text: string };

/** A concept being built locally, before it is saved as gold. */
type DraftConcept = { id: string; facet: SkillFacet; surfaces: string[] };

function errText(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

/**
 * Blind-labelling surface (trial-harness spec §6.7). Shows a criterion and an
 * empty concept list — and nothing from the arms. Cold labels are written before
 * the criterion is adjudicated; the save is one-way (the backend freezes it).
 */
export default function BlindLabelling() {
  const { t } = useTranslation();

  const [sets, setSets] = useState<TrialSetSummary[]>([]);
  const [selectedSetId, setSelectedSetId] = useState<string>('');
  const [batch, setBatch] = useState<BlindBatch | null>(null);
  const [loading, setLoading] = useState(true);

  const [index, setIndex] = useState(0);
  const [concepts, setConcepts] = useState<DraftConcept[]>([]);
  const [surface, setSurface] = useState('');
  const [facet, setFacet] = useState<SkillFacet>('COMPETENCE');
  const [altDrafts, setAltDrafts] = useState<Record<string, string>>({});

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  const [constructLabel, setConstructLabel] = useState('');
  const [constructing, setConstructing] = useState(false);

  const flash = useCallback((next: Toast) => setToast(next), []);
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(id);
  }, [toast]);

  // Load the set list; optionally force-select a set (e.g. a freshly constructed one).
  const loadSets = useCallback(
    async (selectId?: string) => {
      try {
        const list = await fetchTrialSets();
        setSets(list);
        if (selectId) setSelectedSetId(selectId);
        else setSelectedSetId((cur) => cur || (list[0]?.id ?? ''));
      } catch (e) {
        flash({ kind: 'error', text: errText(e, t('skillTrials.blind.loadError')) });
      } finally {
        setLoading(false);
      }
    },
    [flash, t]
  );

  useEffect(() => {
    void loadSets();
  }, [loadSets]);

  const handleConstruct = useCallback(async () => {
    const label = constructLabel.trim();
    if (!label) return;
    setConstructing(true);
    try {
      const res = await constructSet({ label });
      flash({
        kind: 'info',
        text: t('skillTrials.blind.constructed', { members: res.members, blind: res.blind_reserved }),
      });
      setConstructLabel('');
      setLoading(true);
      await loadSets(res.set_id);
    } catch (e) {
      flash({ kind: 'error', text: errText(e, t('skillTrials.blind.constructError')) });
    } finally {
      setConstructing(false);
    }
  }, [constructLabel, flash, t, loadSets]);

  // Load the blind batch whenever the selected set changes.
  const loadBatch = useCallback(
    async (setId: string) => {
      setLoading(true);
      try {
        const next = await fetchBlindBatch(setId);
        setBatch(next);
        setIndex(0);
        setConcepts([]);
      } catch (e) {
        flash({ kind: 'error', text: errText(e, t('skillTrials.blind.loadError')) });
      } finally {
        setLoading(false);
      }
    },
    [flash, t]
  );

  useEffect(() => {
    if (selectedSetId) void loadBatch(selectedSetId);
  }, [selectedSetId, loadBatch]);

  const task = useMemo(() => batch?.pending[index] ?? null, [batch, index]);
  const done = !!batch && index >= batch.pending.length;

  // --- concept editing (local until saved) ---------------------------------

  const addConcept = useCallback(() => {
    const term = surface.trim();
    if (!term) return;
    setConcepts((prev) => [...prev, { id: crypto.randomUUID(), facet, surfaces: [term] }]);
    setSurface('');
  }, [surface, facet]);

  const addSurface = useCallback(
    (conceptId: string) => {
      const term = (altDrafts[conceptId] ?? '').trim();
      if (!term) return;
      setConcepts((prev) =>
        prev.map((c) =>
          c.id === conceptId && !c.surfaces.some((s) => s.toLowerCase() === term.toLowerCase())
            ? { ...c, surfaces: [...c.surfaces, term] }
            : c
        )
      );
      setAltDrafts((prev) => ({ ...prev, [conceptId]: '' }));
    },
    [altDrafts]
  );

  const removeSurface = useCallback((conceptId: string, idx: number) => {
    setConcepts((prev) =>
      prev
        .map((c) => (c.id === conceptId ? { ...c, surfaces: c.surfaces.filter((_, i) => i !== idx) } : c))
        .filter((c) => c.surfaces.length > 0)
    );
  }, []);

  const removeConcept = useCallback((conceptId: string) => {
    setConcepts((prev) => prev.filter((c) => c.id !== conceptId));
  }, []);

  // --- save + advance -------------------------------------------------------

  const save = useCallback(async () => {
    if (!task || concepts.length === 0) return;
    setSaving(true);
    try {
      await saveBlindLabels({
        criterion_id: task.criterion_id,
        criterion_version: task.criterion_version,
        concepts: concepts.map((c) => ({ expected_facet: c.facet, surfaces: c.surfaces })),
      });
      flash({ kind: 'info', text: t('skillTrials.blind.saved') });
      setBatch((prev) => (prev ? { ...prev, labelled: prev.labelled + 1 } : prev));
      setIndex((i) => i + 1);
      setConcepts([]);
      setSurface('');
    } catch (e) {
      flash({ kind: 'error', text: errText(e, t('skillTrials.blind.saveError')) });
    } finally {
      setSaving(false);
    }
  }, [task, concepts, flash, t]);

  // --- render ---------------------------------------------------------------

  const progress = batch
    ? t('skillTrials.blind.progress', { labelled: batch.labelled, total: batch.total })
    : '';

  return (
    <div className="mx-auto max-w-[760px] px-4 py-6" style={{ color: '#0f172a' }}>
      {/* Header + set picker */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">{t('skillTrials.blind.title')}</h1>
          <p className="mt-0.5 text-sm text-slate-500">{t('skillTrials.blind.subtitle')}</p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-slate-500">{t('skillTrials.blind.setLabel')}</span>
          <select
            className="rounded-lg border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            style={{ borderColor: '#cbd5e1' }}
            value={selectedSetId}
            onChange={(e) => setSelectedSetId(e.target.value)}
            disabled={sets.length === 0}
          >
            {sets.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Progress bar */}
      {batch && batch.total > 0 && (
        <div className="mb-4 flex items-center gap-3 text-xs text-slate-500">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-slate-700 transition-[width] duration-300"
              style={{ width: `${Math.round((batch.labelled / batch.total) * 100)}%` }}
            />
          </div>
          <span className="tabular-nums">{progress}</span>
        </div>
      )}

      {/* Sealed banner — the anti-anchoring guarantee */}
      <div
        className="mb-4 flex items-start gap-3 rounded-xl border bg-slate-50 px-4 py-3"
        style={{ borderColor: '#e2e8f0', borderLeft: '3px solid #6b6f8c' }}
      >
        <FontAwesomeIcon icon={faLock} className="mt-0.5 text-slate-500" />
        <p className="text-[13px] leading-snug">
          <span className="font-semibold">{t('skillTrials.blind.sealTitle')}</span>{' '}
          <span className="text-slate-600">{t('skillTrials.blind.sealBody')}</span>
        </p>
      </div>

      {loading ? (
        <div className="rounded-[14px] border bg-white py-12 text-center text-slate-500 shadow-sm" style={{ borderColor: '#e2e8f0' }}>
          {t('common.loading')}
        </div>
      ) : sets.length === 0 ? (
        <div className="rounded-[14px] border bg-white p-6 shadow-sm" style={{ borderColor: '#e2e8f0' }}>
          <div className="mb-1 text-sm font-semibold">{t('skillTrials.blind.noSets')}</div>
          <p className="mb-4 text-sm text-slate-500">{t('skillTrials.blind.constructHint')}</p>
          <div className="flex flex-wrap gap-2">
            <input
              className="min-w-[220px] flex-1 rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              style={{ borderColor: '#cbd5e1' }}
              placeholder={t('skillTrials.blind.constructPlaceholder')}
              value={constructLabel}
              onChange={(e) => setConstructLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleConstruct()}
            />
            <button
              type="button"
              disabled={!constructLabel.trim() || constructing}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
              onClick={handleConstruct}
            >
              <FontAwesomeIcon icon={faWandMagicSparkles} className="text-xs" />
              {constructing ? t('skillTrials.blind.constructing') : t('skillTrials.blind.construct')}
            </button>
          </div>
        </div>
      ) : done ? (
        <div
          className="flex items-center justify-center gap-3 rounded-[14px] border bg-white py-12 text-slate-600 shadow-sm"
          style={{ borderColor: '#e2e8f0' }}
        >
          <FontAwesomeIcon icon={faCircleCheck} className="text-emerald-600" />
          {t('skillTrials.blind.batchComplete')}
        </div>
      ) : task ? (
        <>
          {/* Criterion — all you get */}
          <div className="mb-5 rounded-[14px] border bg-white p-5 shadow-sm" style={{ borderColor: '#e2e8f0' }}>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Chip>{task.language}</Chip>
              <Chip>{task.kind}</Chip>
              <Chip dashed>⚲ {t('skillTrials.blind.versionPinned')}</Chip>
            </div>
            <div className="mb-1.5 text-[10px] uppercase tracking-wider text-slate-400">
              {t('skillTrials.blind.criterionLabel')}
            </div>
            <p className="font-serif text-[17px] leading-relaxed">{task.text}</p>
          </div>

          {/* Concepts being built */}
          <div className="mb-4">
            <div className="mb-2 px-1 text-[13px] font-semibold">
              {t('skillTrials.blind.expectedConcepts')}{' '}
              <span className="font-normal text-slate-400">· {concepts.length}</span>
            </div>
            {concepts.length === 0 ? (
              <div
                className="rounded-xl border border-dashed py-7 text-center text-sm text-slate-400"
                style={{ borderColor: '#cbd5e1' }}
              >
                {t('skillTrials.blind.none')}
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {concepts.map((c) => (
                  <li
                    key={c.id}
                    className="flex flex-wrap items-center gap-2.5 rounded-xl border bg-white px-3 py-2.5 shadow-sm"
                    style={{ borderColor: '#e2e8f0' }}
                  >
                    <span className="min-w-[92px] rounded-md bg-slate-100 px-2 py-0.5 text-center font-mono text-[10px] text-slate-600">
                      {c.facet}
                    </span>
                    <div className="flex flex-1 flex-wrap items-center gap-1.5">
                      {c.surfaces.map((s, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-sm">
                          {i > 0 && <span className="text-slate-400">·</span>}
                          <span className={i === 0 ? 'font-medium' : 'text-slate-500'}>{s}</span>
                          <button
                            type="button"
                            className="text-slate-300 hover:text-red-500"
                            aria-label={t('skillTrials.blind.remove')}
                            onClick={() => removeSurface(c.id, i)}
                          >
                            <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
                          </button>
                        </span>
                      ))}
                      <span className="ml-1 inline-flex items-center gap-1">
                        <input
                          className="w-40 rounded-md border px-2 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-slate-400"
                          style={{ borderColor: '#e2e8f0' }}
                          placeholder={t('skillTrials.blind.altSurfacePlaceholder')}
                          value={altDrafts[c.id] ?? ''}
                          onChange={(e) => setAltDrafts((prev) => ({ ...prev, [c.id]: e.target.value }))}
                          onKeyDown={(e) => e.key === 'Enter' && addSurface(c.id)}
                        />
                        <button
                          type="button"
                          className="rounded-md border border-dashed px-2 py-0.5 text-[11px] text-slate-500 hover:bg-slate-50"
                          style={{ borderColor: '#cbd5e1' }}
                          onClick={() => addSurface(c.id)}
                        >
                          {t('skillTrials.blind.addSurface')}
                        </button>
                      </span>
                    </div>
                    <button
                      type="button"
                      className="text-slate-300 hover:text-red-500"
                      aria-label={t('skillTrials.blind.remove')}
                      onClick={() => removeConcept(c.id)}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Composer */}
          <div className="mb-5 rounded-[14px] border bg-white p-4 shadow-sm" style={{ borderColor: '#e2e8f0' }}>
            <div className="mb-2.5 text-[11px] uppercase tracking-wider text-slate-400">
              {t('skillTrials.blind.addConceptLabel')}
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                className="min-w-[200px] flex-1 rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                style={{ borderColor: '#cbd5e1' }}
                placeholder={t('skillTrials.blind.surfacePlaceholder')}
                value={surface}
                onChange={(e) => setSurface(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addConcept()}
              />
              <select
                className="rounded-lg border px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                style={{ borderColor: '#cbd5e1' }}
                value={facet}
                onChange={(e) => setFacet(e.target.value as SkillFacet)}
              >
                {FACETS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 px-4 py-2 text-sm text-white hover:bg-slate-700"
                onClick={addConcept}
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
                {t('skillTrials.blind.add')}
              </button>
            </div>
          </div>

          {/* Save & next */}
          <div className="flex justify-end">
            <button
              type="button"
              disabled={concepts.length === 0 || saving}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
              onClick={save}
            >
              <FontAwesomeIcon icon={faCheck} className="text-xs" />
              {saving ? t('skillTrials.blind.saving') : t('skillTrials.blind.save')}
            </button>
          </div>
        </>
      ) : (
        <EmptyCard text={t('skillTrials.blind.pickSet')} />
      )}

      {toast && (
        <div
          role="alert"
          className={`fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg px-4 py-2 text-sm text-white shadow-lg ${
            toast.kind === 'error' ? 'bg-red-600' : 'bg-slate-800'
          }`}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}

function Chip({ children, dashed }: { children: React.ReactNode; dashed?: boolean }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 ${
        dashed ? 'border border-dashed' : 'bg-slate-100'
      }`}
      style={dashed ? { borderColor: '#cbd5e1' } : undefined}
    >
      {children}
    </span>
  );
}

function EmptyCard({ text }: { text: string }) {
  return (
    <div className="rounded-[14px] border bg-white py-12 text-center text-sm text-slate-500 shadow-sm" style={{ borderColor: '#e2e8f0' }}>
      {text}
    </div>
  );
}
